import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { config } from '../../shared/environment';
import HttpRoutingService from '../../shared/services/httpRoutingService';
import {
  AddStoreImagesFormDataType,
  BulkDeleteResponse,
  ImageRenamePayload,
  ImageRestrictionResponse,
  InsertImageResponse,
  StorageLimitResponse,
  StoreImageItem,
  StoreImageRenameResponse,
  StoreImagesGalleryResponse,
  SuperadminSettingResponse,
} from '../../components/products/imageGallery/ImageGallery.model';
import { API } from '../../shared/apiRoutes';

interface initialData {
  loading: boolean;
  imagePropertyData: {
    value: ImageRestrictionResponse | null;
    loading: boolean;
    error: string;
  };
  storageAvailableData: {
    value: StorageLimitResponse | null;
    loading: boolean;
    error: string;
  };
  storeImagesData: {
    value: StoreImagesGalleryResponse | null;
    loading: boolean;
    error: string;
  };
  getAdminSettings: {
    loading: boolean;
    error: string;
    value: SuperadminSettingResponse | null;
  };
  addStoreImageData: {
    loading: boolean;
    error: string;
    value: null | InsertImageResponse;
  };
  bulkImageDeleteData: {
    loading: boolean;
    error: string;
    value: null | BulkDeleteResponse;
  }
  imageRenameResponseData: {
    loading: boolean;
    error: string;
    value: null | StoreImageRenameResponse;
  }
  error: string;
  cache: {
    [key: string]: StoreImagesGalleryResponse;
  };
}

interface Payload {
  imageDirectory: string;
  format?: string[];
  size?: number;
}

const initialState: initialData = {
  loading: false,
  imagePropertyData: { value: null, error: '', loading: true },
  storageAvailableData: { value: null, error: '', loading: true },
  getAdminSettings: { loading: true, error: '', value: null },
  addStoreImageData: { loading: true, error: '', value: null },
  error: '',
  storeImagesData: {
    value: null,
    error: '',
    loading: true,
  },
  bulkImageDeleteData: {
    value: null,
    error: '',
    loading: true,
  },
  imageRenameResponseData: {
    value: null,
    error: '',
    loading: true,
  },
  cache: {},
};

//to fetch all image properties data
export const getImageProperty = createAsyncThunk(
  'storeImageGallery/getImageProperty',
  () => {
    return HttpRoutingService.getMethod<ImageRestrictionResponse>(
      `stores/${config.storeId}/properties/productconstants/IMGSIZE`,
      {},
      'v1/',
    );
  },
);

//to optimize image
export const optimizeImage = createAsyncThunk('storeImageGallery/optimizeImage', async (data: { messages: { isVideo: boolean, height: number | undefined, width: number | undefined, key: string | undefined }[] }) => {
  const url = HttpRoutingService.replaceUrl(API.optimizeImage, { storeId: config?.storeId });
  return HttpRoutingService.postMethod<ImageRestrictionResponse>(url, data);
},
);

export const getStoreageAvailability = createAsyncThunk(
  'storeImageGallery/getStoreageAvailability',
  () => {
    return HttpRoutingService.getMethod<StorageLimitResponse>(
      `stores/${config.storeId}/common/storageavailability`,
      {},
      'v1/',
    );
  },
);

export const getAllStoreImages = createAsyncThunk(
  'storeImageGallery/getAllStoreImages',
  async (data: Payload, { getState }) => {
    const cacheKey = JSON.stringify(data);
    const state = getState() as any;
    if (state.storeImageGallery?.cache?.[cacheKey]) {
      return state.storeImageGallery.cache[cacheKey];
    }
    return HttpRoutingService?.getMethod<StoreImagesGalleryResponse>(`stores/${config.storeId}/zenimages`, data, 'v1/');
  },
);

export const getPosAdminSettings = createAsyncThunk(
  'storeImageGallery/getPosAdminSettings',
  () => {
    return HttpRoutingService.getMethod<SuperadminSettingResponse>(
      'superadminsettings/VIDEO_GALLERY',
      {},
      'v1/',
    );
  },
);

//method to delete the bulk images list
export const bulkDeleteImage = createAsyncThunk(
  'storeImageGallery/bulkImageDelete', ({ storeId, data }: { storeId: number, data: { keys: string[] | StoreImageItem[] } }) => {
    const url = HttpRoutingService.replaceUrl('stores/{storeId}/zenimages/delete', { storeId: storeId });
    return HttpRoutingService.putMethod<BulkDeleteResponse>(url, data, false, 'v1/',);
  },
);


export const updateImageDetail = createAsyncThunk(
  'storeImageGallery/updateImageDetail', ({ storeId, data }: { storeId: number, data: ImageRenamePayload }) => {
    const url = HttpRoutingService.replaceUrl('stores/{storeId}/zenimages/rename', { storeId: storeId });
    return HttpRoutingService.postMethod<StoreImageRenameResponse>(url, data, false, "v1/");
  },
);

export const StoreImageGallerySlice = createSlice({
  name: 'storeImageGallery',
  initialState: initialState,
  reducers: {
    clearCache: (state) => {
      state.cache = {};
    }
  },

  extraReducers: builder => {
    builder.addCase(getImageProperty?.pending, state => {
      state.loading = true;
    });
    builder.addCase(
      getImageProperty?.fulfilled,
      (state, action: PayloadAction<ImageRestrictionResponse>) => {
        state.loading = false;
        state.imagePropertyData.value = action?.payload;
      },
    );
    builder.addCase(getImageProperty?.rejected, (state, action) => {
      state.loading = false;
      state.error = action?.error?.message
        ? action?.error?.message
        : 'Error in getImageProperty';
    });
    builder.addCase(getAllStoreImages?.pending, state => {
      state.loading = true;
    });
    builder.addCase(
      getAllStoreImages?.fulfilled,
      (state, action: any) => {
        state.loading = false;
        state.storeImagesData.value = action?.payload;
        const cacheKey = JSON.stringify(action?.meta?.arg);
        state.cache = {
          ...state.cache,
          [cacheKey]: action.payload,
        };
      },
    );
    builder.addCase(getAllStoreImages?.rejected, (state, action) => {
      state.loading = false;
      state.error = action?.error?.message
        ? action?.error?.message
        : 'Error in StoreImagesGalleryResponse';
    });

    builder.addCase(getStoreageAvailability?.pending, state => {
      state.loading = true;
    });
    builder.addCase(
      getStoreageAvailability?.fulfilled,
      (state, action: PayloadAction<StorageLimitResponse>) => {
        state.loading = false;
        state.storageAvailableData.value = action?.payload;
      },
    );
    builder.addCase(getStoreageAvailability?.rejected, (state, action) => {
      state.loading = false;
      state.error = action?.error?.message
        ? action?.error?.message
        : 'Error in getStoreageAvailability';
    });

    builder.addCase(getPosAdminSettings?.pending, state => {
      state.loading = true;
    });
    builder.addCase(
      getPosAdminSettings?.fulfilled,
      (state, action: PayloadAction<SuperadminSettingResponse>) => {
        state.loading = false;
        state.getAdminSettings.value = action?.payload;
      },
    );
    builder.addCase(getPosAdminSettings?.rejected, (state, action) => {
      state.loading = false;
      state.error = action?.error?.message
        ? action?.error?.message
        : 'Error in getPosAdminSettings';
    });

    builder.addCase(bulkDeleteImage?.pending, state => {
      state.loading = true;
    });
    builder.addCase(
      bulkDeleteImage?.fulfilled,
      (state, action: PayloadAction<BulkDeleteResponse>) => {
        state.loading = false;
        state.bulkImageDeleteData.value = action?.payload;
        state.cache = {};
      },
    );
    builder.addCase(bulkDeleteImage?.rejected, (state, action) => {
      state.loading = false;
      state.error = action?.error?.message
        ? action?.error?.message
        : 'Error in bulkDeleteImage';
    });

    builder.addCase(updateImageDetail?.pending, state => {
      state.loading = true;
    });
    builder.addCase(
      updateImageDetail?.fulfilled,
      (state, action: PayloadAction<StoreImageRenameResponse>) => {
        state.loading = false;
        state.imageRenameResponseData.value = action?.payload;
        state.cache = {};
      },
    );
    builder.addCase(updateImageDetail?.rejected, (state, action) => {
      state.loading = false;
      state.error = action?.error?.message
        ? action?.error?.message
        : 'Error in updateImageDetail';
    });
  },
});

export const { clearCache } = StoreImageGallerySlice.actions;
export default StoreImageGallerySlice?.reducer;
